import { CLICK_FREQUENCIES, CONVERSATION_STEP_TYPES, parseSelectors, WIDGET_CHANNEL, type SelectorError, type WorkflowTrigger } from "@sagot/constants";
import { isAction } from "./definition-model";
import type { Problem } from "./problem-model";
import type { Definition, Step } from "./types";

/**
 * The rules of the backend's `graph_triggers.py` for the two P5-8 triggers: `attribute_changed` needs the attribute it
 * watches, and a visitor or lead attribute has no conversation behind it; `visitor_clicks_element` needs a valid
 * selector, runs only in the website widget and takes only the click frequencies (a page visit refuses `every_click`).
 */

const SELECTOR_CODES: Readonly<Record<SelectorError, string>> = {
  empty: "needs_selector",
  syntax: "selector_syntax",
  unsupported: "selector_unsupported",
  too_many: "selector_too_many",
  too_long: "selector_too_long",
};

const clientProblem = (code: string, where: string, extra: Partial<Pick<Problem, "path" | "stepId" | "params">> = {}): Problem => ({ code, where, params: {}, ...extra, source: "client" });

interface Located {
  path: string;
  step: Step;
  action: Step;
}

/** Every behind-the-scenes action, including the ones inside Apply rules (backend `actions_of`). */
function actionsOf(definition: Definition): Located[] {
  return Object.entries(definition.paths).flatMap(([path, body]) =>
    body.steps.flatMap((step): Located[] => {
      if (step.type === "apply_rules") return step.rules.flatMap((rule) => rule.actions.map((action) => ({ path, step, action })));
      return isAction(step) ? [{ path, step, action: step }] : [];
    }),
  );
}

/** Steps that need the conversation a visitor or lead attribute change does not have. */
function conversationStepProblems(definition: Definition): Problem[] {
  const problems: Problem[] = [];
  for (const { path, step, action } of actionsOf(definition)) {
    const needs = CONVERSATION_STEP_TYPES.has(action.type) || (action.type === "set_attribute" && action.object === "conversation");
    if (needs) problems.push(clientProblem("needs_conversation", `step ${action.id}`, { path, stepId: step.id, params: { step: action.id } }));
  }
  for (const [path, body] of Object.entries(definition.paths)) {
    for (const step of body.steps) {
      const where = `step ${step.id}`;
      if (step.type === "snooze") problems.push(clientProblem("needs_conversation", where, { path, stepId: step.id }));
      else if (step.type === "wait" && step.interrupt_on.length > 0) problems.push(clientProblem("no_interrupt_here", where, { path, stepId: step.id }));
    }
  }
  return problems;
}

function attributeProblems(definition: Definition): Problem[] {
  const watched = definition.settings.trigger_options?.attribute;
  if (!watched) return [clientProblem("needs_attribute", "settings")];
  return watched.object === "conversation" ? [] : conversationStepProblems(definition);
}

function clickProblems(definition: Definition): Problem[] {
  const { settings } = definition;
  const problems: Problem[] = [];
  const parsed = parseSelectors(settings.trigger_options?.selector ?? "");
  if (parsed.error !== null) problems.push(clientProblem(SELECTOR_CODES[parsed.error], "settings"));
  if (settings.channels.some((channel) => channel !== WIDGET_CHANNEL)) problems.push(clientProblem("clicks_widget_only", "settings"));
  const frequency = settings.trigger_options?.frequency;
  if (frequency !== undefined && !(CLICK_FREQUENCIES as readonly string[]).includes(frequency)) problems.push(clientProblem("bad_frequency", "settings", { params: { frequency } }));
  return problems;
}

export function triggerProblems(definition: Definition, trigger: WorkflowTrigger): Problem[] {
  if (trigger === "attribute_changed") return attributeProblems(definition);
  if (trigger === "visitor_clicks_element") return clickProblems(definition);
  if (trigger === "visitor_visits_page" && definition.settings.trigger_options?.frequency === "every_click") {
    return [clientProblem("bad_frequency", "settings", { params: { frequency: "every_click" } })];
  }
  return [];
}
